<template>
  <div class="player-form">
    <van-form ref="playerForm" input-align="right" error-message-align="right">
      <div class="title" v-if="!readonlyBasicInfo && !hidePersonalTitle">
        <span>个人信息</span>
        <van-button
          v-if="formReadonly"
          class="player-form-edit-btn"
          size="small"
          type="primary"
          plain
          @click.stop="$emit('edit')"
        >
          编辑
        </van-button>
      </div>
      
      <!-- 姓名（注册类型时由父组件只读展示，此处隐藏） -->
      <van-field 
        v-if="!readonlyBasicInfo"
        :label="getLabel('trueName')" 
        v-model="formData.trueName" 
        :placeholder="`请输入${getLabel('trueName')}`"
        :readonly="formReadonly"
        :rules="formReadonly ? [] : [{ required: true, message: `请输入${getLabel('trueName')}` }]"
      />
      
      <!-- 证件类型 -->
      <van-field 
        v-if="!readonlyBasicInfo && playerCheckboxes.IDCard" 
        :label="getLabel('certType')" 
        v-model="formData.certTypeValue" 
        placeholder="请选择证件类型" 
        :readonly="true"
        clickable 
        @click="formReadonly ? null : showCertTypeFun()" 
        :rules="formReadonly ? [] : [{ required: true, message: '请选择证件类型' }]"
      />
      
      <!-- 证件号码 -->
      <van-field 
        v-if="!readonlyBasicInfo && playerCheckboxes.IDCard" 
        :label="getLabel('IDCardNo')" 
        v-model="formData.IDCardNo" 
        :placeholder="`请输入${getLabel('IDCardNo')}`"
        :readonly="formReadonly"
        :rules="formReadonly ? [] : [{ required: true, message: `请输入${getLabel('IDCardNo')}` }, { validator: checkIDCardNo, message: '请输入正确的证件号码' }]"
      />
      
      <!-- 联系电话 -->
      <van-field 
        v-if="playerCheckboxes.mobile" 
        :label="getLabel('mobile')" 
        v-model="formData.mobile" 
        :placeholder="`请输入${getLabel('mobile')}`"
        :readonly="formReadonly"
        :rules="formReadonly ? [] : [{ required: true, message: `请输入${getLabel('mobile')}` }]"
      />
      
      <!-- 性别 -->
      <van-field 
        v-if="!readonlyBasicInfo && playerCheckboxes.gender" 
        :label="getLabel('gender')" 
        v-model="formData.gender"
        :readonly="formReadonly"
        :rules="formReadonly ? [] : [{ required: true, message: '请选择性别' }]"
      >
        <template #input>
          <van-radio-group v-model="formData.gender" direction="horizontal" :disabled="formReadonly">
            <van-radio name="1" checked-color="#FF6010" icon-size="16px"
              :disabled="formReadonly || checkIDCardNoDis(formData.IDCardNo)">男</van-radio>
            <van-radio name="2" checked-color="#FF6010" icon-size="16px"
              :disabled="formReadonly || checkIDCardNoDis(formData.IDCardNo)">女</van-radio>
          </van-radio-group>
        </template>
      </van-field>
      
      <!-- 出生年月 -->
      <van-field 
        v-if="!readonlyBasicInfo && playerCheckboxes.bmBirthday" 
        :label="getLabel('bmBirthday')" 
        v-model="formData.bmBirthdayValue" 
        placeholder="请选择出生年月"
        :readonly="true"
        clickable 
        @click="formReadonly ? null : showBirthdayFun()" 
        :rules="formReadonly ? [] : [{ required: true, message: '请选择出生年月' }]"
      />
      
      <!-- 报名照（注册类型时由父组件只读展示，此处隐藏） -->
      <div class="my-field" v-if="!readonlyBasicInfo && playerCheckboxes.avatar">
        <van-uploader v-if="!formReadonly" max-count="1" :preview-image="false" :after-read="uploadAvatar" @delete="deleteAvatar">
          <div class="class-logo">
            <div class="img">
              <img src="@/assets/player-default.png" v-if="!formData.avatar" />
              <img :src="imageView(formData.avatar)" v-else />
            </div>
            <div class="logo-info">
              <div class="title2">报名照</div>
              <div class="desc">请上传用户报名照</div>
            </div>
            <van-icon name="arrow" class="icon" />
          </div>
        </van-uploader>
        <div v-else class="class-logo class-logo-readonly">
          <div class="img">
            <img src="@/assets/player-default.png" v-if="!formData.avatar" />
            <img :src="imageView(formData.avatar)" v-else />
          </div>
          <div class="logo-info">
            <div class="title2">报名照</div>
          </div>
        </div>
      </div>
      <!-- 国籍 -->
      <van-field 
        v-if="playerCheckboxes.nationality" 
        :label="getLabel('nationality')" 
        v-model="formData.nationality" 
        :placeholder="`请输入${getLabel('nationality')}`"
        :readonly="formReadonly"
        :rules="formReadonly ? [] : [{ required: true, message: `请输入${getLabel('nationality')}` }]"
      />
      
      <!-- 身份证/护照正面 -->
      <van-field 
        v-if="playerCheckboxes.IDCardPhotoA" 
        label="证件照片正面"
        :readonly="formReadonly"
        :rules="formReadonly ? [] : [{ required: true, message: '证件照片正面' }]"
      >
        <template #input>
          <van-uploader 
            v-if="!formReadonly"
            v-model="formData.IDCardPhotoAList" 
            max-count="1" 
            :after-read="uploadIDCardPhotoA"
            @delete="deleteIDCardPhotoA" 
          />
          <span v-else>{{ formData.IDCardPhotoA ? '已上传' : '-' }}</span>
        </template>
      </van-field>
      
      <!-- 身份证/护照背面 -->
      <van-field 
        v-if="playerCheckboxes.IDCardPhotoB" 
        label="证件照片背面"
        :readonly="formReadonly"
        :rules="formReadonly ? [] : [{ required: true, message: '证件照片背面' }]"
      >
        <template #input>
          <van-uploader 
            v-if="!formReadonly"
            v-model="formData.IDCardPhotoBList" 
            max-count="1" 
            :after-read="uploadIDCardPhotoB"
            @delete="deleteIDCardPhotoB" 
          />
          <span v-else>{{ formData.IDCardPhotoB ? '已上传' : '-' }}</span>
        </template>
      </van-field>
      
      <!-- 球衣号码 -->
      <van-field 
        v-if="playerCheckboxes.number" 
        label="球衣号码" 
        v-model="formData.number" 
        placeholder="请输入球衣号码"
        type="number"
        :readonly="formReadonly"
        :rules="formReadonly ? [] : [{ required: true, message: '请输入球衣号码' }]"
      />
      <div v-if="showNumberRangeTip" class="number-tip">号码范围：{{ numberRangeTipText }}</div>

      <!-- 场上位置 -->
      <van-field 
        v-if="playerCheckboxes.position && sportType !== '足球'" 
        label="场上位置" 
        v-model="formData.position"
        placeholder="请输入场上位置" 
        :readonly="formReadonly"
        :rules="formReadonly ? [] : [{ required: true, message: '请输入场上位置' }]"
      />
      <van-field 
        v-else-if="playerCheckboxes.position" 
        label="场上位置" 
        v-model="formData.position" 
        placeholder="请选择场上位置"
        :readonly="true"
        clickable 
        @click="formReadonly ? null : (showSite = true)" 
        :rules="formReadonly ? [] : [{ required: true, message: '请输入场上位置' }]"
      />

      <!-- 紧急联系人 -->
      <van-field 
        v-if="playerCheckboxes.emergencyContactName" 
        label="紧急联系人" 
        v-model="formData.emergencyContactName"
        placeholder="请输入紧急联系人"
        :readonly="formReadonly"
        :rules="formReadonly ? [] : [{ required: true, message: '请输入紧急联系人' }, { validator: regexName, message: '联系人姓名只能为汉字或字母' }]"
      />
      
      <!-- 是否职业球员 -->
      <van-field 
        v-if="playerCheckboxes.isProfessionalPlayer" 
        label="是否职业球员" 
        v-model="formData.isProfessionalPlayer"
        :readonly="formReadonly"
        :rules="formReadonly ? [] : [{ required: true, message: '请选择是否是职业球员' }]"
      >
        <template #input>
          <van-radio-group v-model="formData.isProfessionalPlayer" direction="horizontal" :disabled="formReadonly">
            <van-radio name="0" checked-color="#FF6010" icon-size="16px">否</van-radio>
            <van-radio name="1" checked-color="#FF6010" icon-size="16px">是</van-radio>
          </van-radio-group>
        </template>
      </van-field>
      
      <!-- 投球主力手 -->
      <van-field 
        v-if="playerCheckboxes.pitchingHand" 
        :label="getLabel('pitchingHand')" 
        v-model="formData.pitchingHand"
        :readonly="formReadonly"
        :rules="formReadonly ? [] : [{ required: true, message: `请选择${getLabel('pitchingHand')}` }]"
      >
        <template #input>
          <van-radio-group v-model="formData.pitchingHand" direction="horizontal" :disabled="formReadonly">
            <van-radio name="左投" checked-color="#FF6010" icon-size="16px">左投</van-radio>
            <van-radio name="右投" checked-color="#FF6010" icon-size="16px">右投</van-radio>
          </van-radio-group>
        </template>
      </van-field>
      
      <!-- 挥棒位置 -->
      <van-field 
        v-if="playerCheckboxes.battingSide" 
        :label="getLabel('battingSide')" 
        v-model="formData.battingSide"
        :readonly="formReadonly"
        :rules="formReadonly ? [] : [{ required: true, message: `请选择${getLabel('battingSide')}` }]"
      >
        <template #input>
          <van-radio-group v-model="formData.battingSide" direction="horizontal" :disabled="formReadonly">
            <van-radio name="左打" checked-color="#FF6010" icon-size="16px">左打</van-radio>
            <van-radio name="右打" checked-color="#FF6010" icon-size="16px">右打</van-radio>
          </van-radio-group>
        </template>
      </van-field>
      
      <!-- 身高 -->
      <van-field 
        v-if="playerCheckboxes.high" 
        label="身高" 
        type="digit" 
        v-model="formData.high" 
        placeholder="请输入身高（CM）"
        :readonly="formReadonly"
        :rules="formReadonly ? [] : [{ required: true, message: '请输入身高（CM）' }, { validator: regexHigh, message: '只能输入0-250数字' }]"
      />
      
      <!-- 体重 -->
      <van-field 
        v-if="playerCheckboxes.weight" 
        type="digit" 
        label="体重" 
        v-model="formData.weight" 
        placeholder="请输入体重（KG）"
        :readonly="formReadonly"
        :rules="formReadonly ? [] : [{ required: true, message: '请输入体重（KG）' }, { validator: regexWeight, message: '只能输入0-200数字' }]"
      />
      
      <!-- 邮箱地址 -->
      <van-field 
        v-if="playerCheckboxes.bmEmail" 
        :label="getLabel('bmEmail')" 
        v-model="formData.bmEmail" 
        placeholder="请输入邮箱地址"
        :readonly="formReadonly"
        :rules="formReadonly ? [] : [{ required: true, message: '请输入邮箱地址' }, { validator: isEmail, message: '请输入邮箱地址' }]"
      />
      
      <!-- 球衣姓名 -->
      <van-field 
        v-if="playerCheckboxes.jerseyName" 
        :label="getLabel('jerseyName')" 
        v-model="formData.jerseyName" 
        :placeholder="`请输入${getLabel('jerseyName')}`"
        :readonly="formReadonly"
        :rules="formReadonly ? [] : [{ required: true, message: '请输入' + getLabel('jerseyName') }, { validator: regexTeamName, message: getLabel('jerseyName') + '最多输入20个字符' }]"
      />
      
      <!-- 球衣尺码 -->
      <JerseySize 
        v-if="playerCheckboxes.jerseysSize && !formReadonly" 
        :card="jerseysSizeObj.card" 
        :arr="jerseysSizeObj.arr"
        :text="formData.jerseysSize" 
        @update="updateJerseysSize" 
      />
      <van-field v-else-if="playerCheckboxes.jerseysSize && formReadonly" label="球衣尺码" readonly :value="formData.jerseysSize || '-'" />
      
      <!-- 球鞋尺码 -->
      <van-field 
        v-if="playerCheckboxes.shoesSize" 
        label="球鞋尺码" 
        v-model="formData.shoesSize" 
        placeholder="请输入球鞋尺码"
        :readonly="formReadonly"
        :rules="formReadonly ? [] : [{ required: true, message: '请输入球鞋尺码' }, { validator: regexShoesSize, message: '只能输入20-50数字' }]"
      />
      
      <!-- 资质证明 -->
      <van-field 
        v-if="playerCheckboxes.qualificationFile" 
        input-align="left" 
        :label="getLabel('qualificationFile')"
        :readonly="formReadonly"
        :rules="formReadonly ? [] : [{ required: true, message: '请上传资质证明' }]" 
        style="display: block;"
      >
        <template #input>
          <div class="my-imgs" v-if="!formReadonly">
            <div style="font-size: 12px;margin-bottom: 6px;">最多上传5张图片</div>
            <div style="padding-left: 12px;">
              <van-uploader 
                v-model="formData.qualificationFileList" 
                max-count="5" 
                :after-read="uploadQualificationFile"
                @delete="deleteQualificationFile" 
              />
            </div>
          </div>
          <span v-else>{{ (formData.qualificationFileList && formData.qualificationFileList.length) ? '已上传' : '-' }}</span>
        </template>
      </van-field>
      
      <!-- 保险证明 -->
      <van-field 
        v-if="playerCheckboxes.insureFile" 
        :label="getLabel('insureFile')"
        :readonly="formReadonly"
        :rules="formReadonly ? [] : [{ required: true, message: '请上传保险证明' }]"
      >
        <template #input>
          <van-uploader 
            v-if="!formReadonly"
            v-model="formData.insureFileList" 
            max-count="1" 
            :after-read="uploadInsureFile"
            @delete="deleteInsureFile" 
          />
          <span v-else>{{ formData.insureFile ? '已上传' : '-' }}</span>
        </template>
      </van-field>
      
      <!-- 就读学校 -->
      <van-field 
        v-if="playerCheckboxes.school" 
        label="就读学校" 
        v-model="formData.school" 
        placeholder="请输入就读学校"
        :readonly="formReadonly"
        :rules="formReadonly ? [] : [{ required: true, message: '请输入就读学校' }]"
      />
      
      <!-- 备注（与「自我评价」同布局：标题 + 多行 + 字数） -->
      <div class="self-introduction" v-if="playerCheckboxes.remark">
        <div class="title2">{{ getLabel('remark') }}</div>
        <div class="content">
          <van-field
            input-align="left"
            rows="2"
            autosize
            show-word-limit
            v-model="formData.remark"
            :placeholder="`请输入${getLabel('remark')}`"
            maxlength="200"
            :readonly="formReadonly"
            :rules="formReadonly ? [] : [{ required: true, message: `请输入${getLabel('remark')}` }]"
            type="textarea"
          />
        </div>
      </div>
      
      <!-- 所在地区 -->
      <van-field 
        v-if="playerCheckboxes.domicileAreaId" 
        :label="getLabel('domicileAreaId')" 
        v-model="formData.domicileAreaName" 
        readonly
        clickable
        @click="formReadonly ? null : (showArea = true)" 
        :placeholder="`请选择${getLabel('domicileAreaId')}`" 
        :rules="formReadonly ? [] : [{ required: true, message: '请选择' + getLabel('domicileAreaId') }]"
      />
      
      <!-- 民族 -->
      <van-field 
        v-if="playerCheckboxes.ethnicity" 
        label="民族" 
        v-model="formData.ethnicity" 
        placeholder="请输入民族"
        :readonly="formReadonly"
        :rules="formReadonly ? [] : [{ required: true, message: '请输入民族' }]"
      />
      
      <!-- 队内职务 -->
      <van-field 
        v-if="playerCheckboxes.title" 
        label="队内职务" 
        v-model="formData.title" 
        placeholder="请输入队内职务"
        :readonly="formReadonly"
        :rules="formReadonly ? [] : [{ required: true, message: '请输入队内职务' }]"
      />
      
      <!-- 工作单位 -->
      <van-field 
        v-if="playerCheckboxes.enterprise" 
        label="工作单位" 
        v-model="formData.enterprise" 
        placeholder="请输入工作单位"
        :readonly="formReadonly"
        :rules="formReadonly ? [] : [{ required: true, message: '请输入工作单位' }]"
      />
      
      <!-- 职业 -->
      <van-field 
        v-if="playerCheckboxes.post" 
        label="职业" 
        v-model="formData.post" 
        placeholder="请输入职业"
        :readonly="formReadonly"
        :rules="formReadonly ? [] : [{ required: true, message: '请输入职业' }]"
      />
      
      <!-- 家长姓名 -->
      <van-field 
        v-if="playerCheckboxes.guardianName" 
        :label="getLabel('guardianName')"
        v-model="formData.guardianName" 
        :placeholder="`请输入${getLabel('guardianName')}`"
        :readonly="formReadonly"
        :rules="formReadonly ? [] : [{ required: true, message: `请输入${getLabel('guardianName')}` }, { validator: regexName, message: `${getLabel('guardianName')}只能为汉字或字母` }]"
      />
      
      <!-- 家长手机号 -->
      <van-field 
        v-if="playerCheckboxes.guardianMobile" 
        :label="getLabel('guardianMobile')"
        v-model="formData.guardianMobile"
        :placeholder="`请输入${getLabel('guardianMobile')}`"
        :readonly="formReadonly"
        :rules="formReadonly ? [] : [{ required: true, message: `请输入${getLabel('guardianMobile')}` }, { validator: checkMobile, message: '手机号格式不正确' }]"
      />
      
      <!-- 所属球队 -->
      <van-field 
        v-if="playerCheckboxes.joinTeam" 
        :label="getLabel('joinTeam')" 
        v-model="formData.joinTeam" 
        :placeholder="`请输入${getLabel('joinTeam')}`"
        :readonly="formReadonly"
        :rules="formReadonly ? [] : [{ required: true, message: '请输入' + getLabel('joinTeam') }]"
      />
      
      <!-- 注册证号 -->
      <van-field 
        v-if="playerCheckboxes.serialNumber" 
        :label="getLabel('serialNumber')" 
        v-model="formData.serialNumber" 
        :placeholder="`请输入${getLabel('serialNumber')}`"
        :readonly="formReadonly"
        :rules="formReadonly ? [] : [{ required: true, message: `请输入${getLabel('serialNumber')}` }]"
      />

      <!-- 自我评价 -->
      <!-- <van-field 
        v-if="playerCheckboxes.selfIntroduction" 
        label="自我评价" 
        rows="2"
        autosize
        show-word-limit
        v-model="formData.selfIntroduction" 
        placeholder="请输入自我评价"
        maxlength="200" 
        type="textarea"
      /> -->
      <div class="self-introduction" v-if="playerCheckboxes.selfIntroduction">
        <div class="title2">自我评价</div>
        <div class="content">
          <van-field 
            input-align="left"
            rows="2"
            autosize
            show-word-limit
            v-model="formData.selfIntroduction" 
            placeholder="请输入自我评价"
            maxlength="200"
            :rules="[{ required: true, message: '请输入自我评价' }]"
            type="textarea"
          /> 
        </div>
      </div>

      <!-- 自定义单选1 -->
      <van-field 
        v-if="playerCheckboxes.customText1" 
        :label="getLabel('customText1')" 
        v-model="formData.customText1" 
        readonly
        @click="showCustomTextFun('customText1')" 
        :placeholder="`请选择${getLabel('customText1')}`"
        :rules="[{ required: true, message: `请选择${getLabel('customText1')}` }]"
      />

      <!-- 自定义单选2 -->
      <van-field 
        v-if="playerCheckboxes.customText2" 
        :label="getLabel('customText2')" 
        v-model="formData.customText2" 
        readonly
        @click="showCustomTextFun('customText2')" 
        :placeholder="`请选择${getLabel('customText2')}`"
        :rules="[{ required: true, message: `请选择${getLabel('customText2')}` }]"
      />

      <!-- 自定义单选3 -->
      <van-field 
        v-if="playerCheckboxes.customText3" 
        :label="getLabel('customText3')" 
        v-model="formData.customText3" 
        readonly
        @click="showCustomTextFun('customText3')" 
        :placeholder="`请选择${getLabel('customText3')}`"
        :rules="[{ required: true, message: `请选择${getLabel('customText3')}` }]"
      />
    </van-form>

    <!-- 弹窗组件 -->
    <div class="popup">
      <!-- 出生年月选择器 -->
      <van-popup v-model="showBirthday" position="bottom" :style="{ height: '50%' }">
        <van-datetime-picker 
          v-model="currentDate" 
          type="date" 
          title="选择年月日" 
          :min-date="minDate" 
          :max-date="maxDate"
          @cancel="cancelBirthdayTime" 
          @confirm="confirmBirthdayTime" 
        />
      </van-popup>
      
      <!-- 证件类型选择器 -->
      <van-popup v-model="showCertType" position="bottom" :style="{ height: '50%' }">
        <van-picker 
          title="请选择证件类型" 
          show-toolbar 
          :columns="certTypeColumns" 
          value-key="label" 
          @confirm="onConfirmCertType"
          @cancel="onCancelCertType" 
        />
      </van-popup>
      
      <!-- 地区选择器 -->
      <van-popup v-model="showArea" round position="bottom">
        <van-cascader 
          v-model="formData.domicileAreaId" 
          :title="`请选择${getLabel('domicileAreaId')}`" 
          :options="options" 
          :field-names="fieldNames"
          @close="showArea = false" 
          @change="onChange" 
          @finish="onFinish" 
        />
      </van-popup>
      
      <!-- 场上位置选择器 -->
      <van-popup v-model="showSite" round position="bottom" :style="{ height: '50%' }">
        <van-picker 
          title="请选择位置" 
          show-toolbar 
          :columns="siteList"
          @confirm="(e) => { formData.position = e, showSite = false }" 
          @cancel="showSite = false" 
        />
      </van-popup>

      <!-- 自定义单选选择器 -->
      <van-popup v-model="showCustomText" round position="bottom" :style="{ height: '50%' }">
        <van-picker 
          title="请选择自定义单选" 
          show-toolbar 
          :columns="customTextOptionsObj[customTextType] || []" 
          @confirm="onConfirmCustomText" 
          @cancel="showCustomText = false" 
        />
      </van-popup>
    </div>
  </div>
</template>

<script>
import { DateUtil, QNImageUtil, RegExpr, ToolUtil } from "@/utils";
import Vue from 'vue'
import {
  Form, Field, Icon, Popup, Loading, Overlay, Dialog, Uploader,
  RadioGroup, Radio, DatetimePicker, Button, Picker, Toast, Cascader,
  Checkbox, CheckboxGroup
} from "vant";
import { uploadImage } from "@/api/qiniu";
import { getAreaTreeList } from "@/api/match";
import JerseySize from '@/components/JerseySize'
import { playerSignupFieldsLabel as playerSignupFieldLabelDefaults } from '@/config'

Vue.use(Form).use(Field).use(Icon).use(Popup)
  .use(Loading).use(Overlay).use(Dialog).use(Uploader).use(RadioGroup)
  .use(Radio).use(Button).use(DatetimePicker).use(Picker).use(Cascader).use(Checkbox).use(CheckboxGroup)

export default {
  name: 'PlayerForm',
  components: {
    JerseySize
  },
  props: {
    // 表单数据源
    form: {
      type: Object,
      default: () => ({})
    },
    // 控制显示哪些表单项
    playerSignupFields: {
      type: Array,
      default: () => []
    },
    // 球衣尺码相关配置
    jerseysSizeFields: {
      type: String,
      default: ''
    },
    jerseysSizeAccessory: {
      type: String,
      default: ''
    },
    // 球衣号码范围（形如 "1|99"，-1|-1 表示不限）
    playerNumberRange: {
      type: String,
      default: ''
    },
    // 证件类型选项
    certTypeColumns: {
      type: Array,
      default: () => []
    },
    // 动态标题 label；未配置的键在 getLabel 中回退到 @/config 的 playerSignupFieldsLabel
    playerSignupFieldsLabel: {
      type: Object,
      default: () => ({})
    },
    // 运动类型
    sportType: {
      type: String,
      default: ''
    },
    // 年龄限制
    maxAge: {
      type: String,
      default: ''
    },
    minAge: {
      type: String,
      default: ''
    },
    // 七牛云token
    qiniuToken: {
      type: String,
      default: ''
    },
    // 性别限制
    genderLimit: {
      type: [String, Number],
      default: null
    },
    // 自定义单选选项
    customTextOptions: {
      type: String,
      default: ''
    },
    // 注册球队报名类型：隐藏基本信息（姓名、证件类型、证件号码、性别、出生日期、报名照），由父组件只读展示
    readonlyBasicInfo: {
      type: Boolean,
      default: false
    },
    // 整表只读（查看态）
    formReadonly: {
      type: Boolean,
      default: false
    },
    /** 仅隐藏「个人信息」区块标题（顶部已有卡片等入口时避免重复） */
    hidePersonalTitle: {
      type: Boolean,
      default: false
    }
  },
  computed: {
    parsedNumberRange() {
      const raw = (this.playerNumberRange || '').toString().trim()
      if (!raw || raw.indexOf('|') === -1) return null
      const [minRaw, maxRaw] = raw.split('|')
      const min = Number(minRaw)
      const max = Number(maxRaw)
      if (!Number.isFinite(min) || !Number.isFinite(max)) return null
      if (min === -1 && max === -1) return null
      return { min, max }
    },
    showNumberRangeTip() {
      return !!this.playerCheckboxes.number && !!this.parsedNumberRange
    },
    numberRangeTipText() {
      if (!this.parsedNumberRange) return ''
      return `${this.parsedNumberRange.min} - ${this.parsedNumberRange.max}`
    }
  },
  data() {
    return {
      formData: {},
      playerCheckboxes: {},
      jerseysSizeObj: {},
      showBirthday: false,
      showCertType: false,
      showArea: false,
      showSite: false,
      currentDate: '',
      minDate: '',
      maxDate: '',
      options: [],
      fieldNames: {
        text: 'name',
        value: 'id',
        children: 'kids',
      },
      siteList: ['前锋', '中场', '后卫', '门将'],
      showCustomText: false,
      customTextType: '',
      customTextOptionsObj: {}
    }
  },
  watch: {
    form: {
      handler(newVal) {
        this.formData = JSON.parse(JSON.stringify(newVal))
        console.log(this.certTypeColumns);
        
        this.$set(this.formData,'certTypeValue',this.certTypeColumns.find(item => item.value === newVal.certType)?.label || '')
      },
      immediate: true,
      deep: true
    },
    playerSignupFields: {
      handler(newVal) {
        this.initPlayerCheckboxes(newVal)
      },
      immediate: true
    },
    jerseysSizeFields: {
      handler(newVal) {
        this.initJerseysSizeObj(newVal)
      },
      immediate: true
    },
    'formData.IDCardNo': {
      handler() {
        if (RegExpr.isIdCard(this.formData.IDCardNo)) {
          const player = JSON.parse(JSON.stringify(this.formData))
          player.gender = '' + ToolUtil.getSex(player.IDCardNo)
          player.bmBirthdayValue = ToolUtil.getBirthday(player.IDCardNo)
          player.bmBirthday = DateUtil.parseDateTime(ToolUtil.getBirthday(this.formData.IDCardNo), '{y}-{m}-{d}T00:00:00+0800')
          this.formData = player
          this.$emit('update:form', player)
        }
      }
    },
    'formData.gender': {
      handler() {
        // 性别验证逻辑
        if (this.playerCheckboxes.gender && this.genderLimit && this.genderLimit !== 0 && this.genderLimit !== 3 && this.formData.gender != this.genderLimit) {
          Toast({ type: 'fail', message: '性别异常' })
        }
      }
    },
    customTextOptions: {
      handler(newVal) {
        if (!newVal) return
        this.customTextOptionsObj = JSON.parse(newVal) || {}
      },
      immediate: true
    }
  },
  mounted() {
    this.initDateRange()
    this.getAreaTreeList()
    this.initFileLists()
  },
  methods: {
    showCustomTextFun(type) {
      this.customTextType = type
      this.showCustomText = true
    },
    onConfirmCustomText(value) {
      console.log('value', value)
      // this.formData[this.customTextType] = value
      this.$set(this.formData, this.customTextType, value)
      this.showCustomText = false
      this.$emit('update:form', this.formData)
    },
    imageView(img) {
      if (!img) return ''
      return QNImageUtil.imageView(img)
    },
    // 初始化表单项显示控制
    initPlayerCheckboxes(fields) {
      const playerCheckboxes = {}
      if (Array.isArray(fields)) {
        fields.forEach(field => {
          playerCheckboxes[field] = true
        })
      }
      this.playerCheckboxes = playerCheckboxes
    },
    
    // 初始化球衣尺码配置
    initJerseysSizeObj(fields) {
      this.jerseysSizeObj = {
        card: this.jerseysSizeAccessory,
        arr: fields ? fields.split(',') : []
      }
    },
    
    // 初始化文件上传列表
    initFileLists() {
      // 初始化报名照列表
      if (this.formData.avatar && !this.formData.avatarList) {
        this.formData.avatarList = [{ url: QNImageUtil.imageView(this.formData.avatar), isImage: true, hash: this.formData.avatar }]
      }
      
      // 初始化证件照正面列表
      if (this.formData.IDCardPhotoA && !this.formData.IDCardPhotoAList) {
        this.formData.IDCardPhotoAList = [{ url: QNImageUtil.imageView(this.formData.IDCardPhotoA), isImage: true, hash: this.formData.IDCardPhotoA }]
      }
      
      // 初始化证件照反面列表
      if (this.formData.IDCardPhotoB && !this.formData.IDCardPhotoBList) {
        this.formData.IDCardPhotoBList = [{ url: QNImageUtil.imageView(this.formData.IDCardPhotoB), isImage: true, hash: this.formData.IDCardPhotoB }]
      }
      
      // 初始化资质证明列表
      if (this.formData.qualificationFile && !this.formData.qualificationFileList) {
        const files = this.formData.qualificationFile.split(',')
        this.formData.qualificationFileList = files.map(file => ({
          url: QNImageUtil.imageView(file),
          isImage: true,
          hash: file
        }))
      }
      
      // 初始化保险证明列表
      if (this.formData.insureFile && !this.formData.insureFileList) {
        const files = this.formData.insureFile.split(',')
        this.formData.insureFileList = files.map(file => ({
          url: QNImageUtil.imageView(file),
          isImage: true,
          hash: file
        }))
      }
    },
    
    // 初始化日期范围
    initDateRange() {
      this.minDate = this.maxAge ? new Date(this.maxAge) : new Date(1970, 1, 1)
      this.maxDate = this.minAge ? new Date(this.minAge) : new Date()
    },
    
    // 获取动态标签（接口可覆盖；无自定义时用语义化默认文案，避免展示英文字段名）
    getLabel(type) {
      const o = this.playerSignupFieldsLabel
      return (o && o[type]) || playerSignupFieldLabelDefaults[type] || type
    },
    
    // 验证方法
    regexNumber(value) {
      return value >= 0 && value <= 99
    },
    regexShoesSize(value) {
      return value >= 20 && value <= 50
    },
    regexHigh(value) {
      return value >= 0 && value <= 250
    },
    regexWeight(value) {
      return value >= 0 && value <= 200
    },
    isEmail(value) {
      return RegExpr.isEmail(value)
    },
    regexName(value) {
      let regex = /^[A-Za-z\u4e00-\u9fa5\s]+$/
      return regex.test(value)
    },
    regexTeamName(value) {
      const length = this.strLength(value)
      return length <= 20
    },
    strLength(word) {
      let length = 0
      if (typeof word === 'number') {
        length = word.toString().length
      } else if (word === null) {
        length = 3
      } else if (typeof word === 'undefined') {
        length = 0
      } else {
        Array.from(word).map(function (char) {
          if (char.charCodeAt(0) > 255) {
            length += 2
          } else {
            length++
          }
        })
      }
      return length
    },
    checkIDCardNoDis(value) {
      return RegExpr.isIdCard(value)
    },
    checkMobile(value) {
      return RegExpr.checkMobile(value)
    },
    checkIDCardNo(value) {
      if (this.formData.certType === 1) return RegExpr.isIdCard(value)
      return true
    },
    
    // 弹窗控制方法
    showBirthdayFun() {
      if (RegExpr.isIdCard(this.formData.IDCardNo)) return
      this.showBirthday = true
    },
    showCertTypeFun() {
      this.showCertType = true
    },
    
    // 日期选择器事件
    confirmBirthdayTime(value) {
      this.formData.bmBirthdayValue = DateUtil.parseDateTime(value, '{y}-{m}-{d}')
      this.formData.bmBirthday = DateUtil.parseDateTime(value, '{y}-{m}-{d}T00:00:00+0800')
      this.showBirthday = false
      this.$emit('update:form', this.formData)
    },
    cancelBirthdayTime() {
      this.showBirthday = false
    },
    
    // 证件类型选择器事件
    onConfirmCertType(value) {
      // this.formData.certTypeValue = value.label
      this.$set(this.formData,'certTypeValue',value.label)
      this.formData.certType = value.value
      this.showCertType = false
      this.$emit('update:form', this.formData)
    },
    onCancelCertType() {
      this.showCertType = false
    },
    
    // 地区选择器事件
    onChange({ selectedOptions }) {
      const a = []
      selectedOptions.forEach(item => {
        a.push(item.name)
      })
      this.formData.domicileAreaName = a.join('/')
      this.$emit('update:form', this.formData)
    },
    onFinish({ selectedOptions }) {
      const a = []
      selectedOptions.forEach(item => {
        a.push(item.name)
      })
      this.formData.domicileAreaName = a.join('/')
      this.showArea = false
      this.$emit('update:form', this.formData)
    },
    
    // 获取地区数据
    getAreaTreeList() {
      getAreaTreeList().then(res => {
        if (res) {
          const { data } = res
          this.options = JSON.parse(data.list.list.replaceAll("'", '"')) || []
          setTimeout(() => {
            if (this.formData.domicileAreaId) { 
              let a = []
              a = this.getAreaInfo(this.form.domicileAreaId, this.options, a)
              a.reverse()
              this.formData.domicileAreaName = a.join('/')
            }
          },10)
        }
      })
    },
    // 获取地区信息
    getAreaInfo(areaId, options, a) {
      if (!areaId) return ''
      for (let i = 0; i < options.length; i++) {
        if (options[i].id === areaId) {
          a.push(options[i].name)
          return a
        } else if (options[i].kids) {
          const b = this.getAreaInfo(areaId, options[i].kids, a)
          if (b) {
            a.push(options[i].name)
            return a
          }
        }
      }
      return false
    },
    
    // 文件上传方法
    uploadAvatar(file) {
      const formData = new FormData()
      formData.append('token', this.qiniuToken)
      formData.append('file', file.file)
      uploadImage(formData).then(res => {
        this.formData.avatar = res.hash
        this.$emit('update:form', this.formData)
      })
    },
    deleteAvatar() {
      this.formData.avatar = ''
      this.$emit('update:form', this.formData)
    },
    
    uploadIDCardPhotoA(file) {
      const formData = new FormData()
      formData.append('token', this.qiniuToken)
      formData.append('file', file.file)
      uploadImage(formData).then(res => {
        this.formData.IDCardPhotoA = res.hash
        this.$emit('update:form', this.formData)
      })
    },
    deleteIDCardPhotoA() {
      this.formData.IDCardPhotoA = ''
      this.$emit('update:form', this.formData)
    },
    
    uploadIDCardPhotoB(file) {
      const formData = new FormData()
      formData.append('token', this.qiniuToken)
      formData.append('file', file.file)
      uploadImage(formData).then(res => {
        this.formData.IDCardPhotoB = res.hash
        this.$emit('update:form', this.formData)
      })
    },
    deleteIDCardPhotoB() {
      this.formData.IDCardPhotoB = ''
      this.$emit('update:form', this.formData)
    },
    
    uploadQualificationFile(file) {
      const formData = new FormData()
      formData.append('token', this.qiniuToken)
      formData.append('file', file.file)
      uploadImage(formData).then(res => {
        const player = JSON.parse(JSON.stringify(this.formData))
        if (player.qualificationFile && player.qualificationFile.length > 0) {
          player.qualificationFile = player.qualificationFile + ',' + res.hash
        } else {
          player.qualificationFile = res.hash
        }
        const a = player.qualificationFile.split(',')
        player.qualificationFileList = []
        a.forEach(item => {
          player.qualificationFileList.push({ url: QNImageUtil.imageView(item), isImage: true, hash: item })
        })
        this.formData = player
        this.$emit('update:form', this.formData)
      })
    },
    deleteQualificationFile() {
      const player = JSON.parse(JSON.stringify(this.formData))
      const a = []
      player.qualificationFileList.forEach(item => {
        a.push(item.hash)
      })
      player.qualificationFile = a.join(',')
      this.formData = player
      this.$emit('update:form', this.formData)
    },
    
    uploadInsureFile(file) {
      const formData = new FormData()
      formData.append('token', this.qiniuToken)
      formData.append('file', file.file)
      uploadImage(formData).then(res => {
        const player = JSON.parse(JSON.stringify(this.formData))
        if (player.insureFile && player.insureFile.length > 0) {
          player.insureFile = player.insureFile + ',' + res.hash
        } else {
          player.insureFile = res.hash
        }
        const a = player.insureFile.split(',') || []
        player.insureFileList = []
        a.forEach(item => {
          player.insureFileList.push({ url: QNImageUtil.imageView(item), isImage: true, hash: item })
        })
        this.formData = player
        this.$emit('update:form', this.formData)
      })
    },
    deleteInsureFile() {
      const player = JSON.parse(JSON.stringify(this.formData))
      const a = []
      player.insureFileList.forEach(item => {
        a.push(item.hash)
      })
      player.insureFile = a.join(',')
      this.formData = player
      this.$emit('update:form', this.formData)
    },
    
    /** 供多选手 Tab 等场景在提交前从子组件取全量表单（普通输入未必已 emit） */
    getFormData() {
      return JSON.parse(JSON.stringify(this.formData))
    },

    // 表单验证方法
    validate() {
      this.$emit('update:form', this.formData)
      return this.$refs.playerForm.validate()
    },

    // 更新球衣尺码
    updateJerseysSize(text) {
      // this.formData.jerseysSize = text
      this.$set(this.formData, 'jerseysSize', text)
      this.$emit('update:form', this.formData)
    }
  }
}
</script>

<style scoped lang="scss">
.player-form {
  .title {
    display: flex;
    align-items: center;
    justify-content: space-between;
    height: 43px;
    line-height: 43px;
    font-size: 16px;
    color: #909399;
    padding: 0 15px;
  }
  .player-form-edit-btn {
    width: 68px;
    height: 30px;
    padding: 0;
    font-size: 13px;
    color: var(--bg3);
    background-color: #E9E9EB;
    border: none;
  }
}
.self-introduction{
  .title2{
            background-color: #fff;
    color: rgba(0, 0, 0, 0.9);
    font-size: 14px;
    height: 40px;
    line-height: 40px;
    padding: 0 15px;
    box-sizing: border-box;
  }
}
::v-deep .van-cell{
  min-height: 54px;
  box-sizing: border-box;
  align-items: center;
}
::v-deep .van-field__label{
  color: rgba(0, 0, 0, 0.9);
}
::v-deep .van-field__control{
  font-weight: 500;
}
::v-deep .van-uploader{
  width: 100% !important;
}
::v-deep .van-uploader__wrapper{
  display: flex;
  justify-content: flex-end;
}
.my-field{
  width: 100%;
  background-color: #fff;
  border-bottom: 1px solid #EEEEEE;
  ::v-deep .van-uploader__wrapper{
    display: block !important;
  }
  .class-logo{
    width: 100%;
    height: 74px;
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 0 16px;
    box-sizing: border-box;
    .img{
      width: 48px;
      height: 48px;
      border-radius: 24px;
      background: #D8D8D8;
      img{
        width: 100%;
        height: 100%;
        object-fit: cover;
        border-radius: 50%;
      }
    }
    .logo-info{
      margin-left: 8px;
      flex: 1;
      .title2{
        font-size: 16px;
        font-weight: 500;
        color: rgba(0, 0, 0, 0.9);
      }
      .desc{
        font-size: 14px;
        font-weight: 500;
        color: rgba(0, 0, 0, 0.4);
      }
    }
    .icon{
      color: #BDBDBD;
      font-size: 24px;
    }
  }
}
.number-tip{
  padding-bottom: 2px;
  transform: translateY(-2px);
  text-align: left;
  color: #999;
  font-size: 12px;
  background-color: #fff;
  border-bottom: 1px solid #ebedf0;
  padding-right: 2px;
  padding-left: 16px;
}
::v-deep .my-imgs {
  width: 100%;
}
</style>
